import React, { useState } from 'react';
import { Stack, Text, Group, Box, Card, Progress, SegmentedControl, ScrollArea, SimpleGrid, Image } from '@mantine/core';
import { CheckCircle2, Circle, Camera } from 'lucide-react';
import type { ConstructionMilestone } from '../types';
// Mock data
const projectMilestones: ConstructionMilestone[] = [{
  id: '1',
  title: 'Foundation Complete',
  description: 'Piling and foundation work finished',
  progress: 100,
  completedDate: '2023-03-15',
  expectedDate: '2023-03-01',
  photos: ['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&auto=format&fit=crop']
}, {
  id: '2',
  title: 'Structure - Floors 1-10',
  description: 'Concrete structure for lower floors',
  progress: 100,
  completedDate: '2023-07-20',
  expectedDate: '2023-07-15',
  photos: ['https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1590725121839-892b458a74fe?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&auto=format&fit=crop']
}, {
  id: '3',
  title: 'Structure - Floors 11-20',
  description: 'Concrete structure for upper floors',
  progress: 75,
  expectedDate: '2023-11-30',
  photos: ['https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&auto=format&fit=crop']
}, {
  id: '4',
  title: 'MEP Installation',
  description: 'Mechanical, electrical, and plumbing',
  progress: 40,
  expectedDate: '2024-02-28',
  photos: ['https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&auto=format&fit=crop']
}, {
  id: '5',
  title: 'Finishing Works',
  description: 'Interior finishing and fixtures',
  progress: 0,
  expectedDate: '2024-06-30'
}];
const unitMilestones: ConstructionMilestone[] = [{
  id: '1',
  title: 'Floor Slab Complete',
  description: 'Unit floor structure finished',
  progress: 100,
  completedDate: '2023-08-10',
  expectedDate: '2023-08-01',
  photos: ['https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&auto=format&fit=crop']
}, {
  id: '2',
  title: 'Internal Walls',
  description: 'Partition walls and framing',
  progress: 100,
  completedDate: '2023-09-25',
  expectedDate: '2023-09-20',
  photos: ['https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1590725121839-892b458a74fe?w=400&auto=format&fit=crop']
}, {
  id: '3',
  title: 'Electrical Rough-In',
  description: 'Wiring and electrical boxes',
  progress: 60,
  expectedDate: '2023-11-15',
  photos: ['https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&auto=format&fit=crop']
}, {
  id: '4',
  title: 'Plumbing Rough-In',
  description: 'Pipes and drainage installation',
  progress: 45,
  expectedDate: '2023-11-30',
  photos: ['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&auto=format&fit=crop']
}, {
  id: '5',
  title: 'Flooring & Tiling',
  description: 'Floor tiles and finishing',
  progress: 0,
  expectedDate: '2024-01-30'
}];
const galleryPhotos = ['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&auto=format&fit=crop', 'https://images.unsplash.com/photo-1590725121839-892b458a74fe?w=400&auto=format&fit=crop'];
export function ConstructionTracker() {
  const [view, setView] = useState<'project' | 'unit'>('project');
  const milestones = view === 'project' ? projectMilestones : unitMilestones;
  const overallProgress = Math.round(milestones.reduce((acc, m) => acc + m.progress, 0) / milestones.length);
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <ScrollArea h="100%" offsetScrollbars data-id="element-132">
      <Stack gap="lg" pb={100} data-id="element-133">
        {/* Header */}
        <Box data-id="element-134">
          <Text fw={700} size="xl" c="#1a2332" mb={4} data-id="element-135">
            Construction Progress
          </Text>
          <Text size="sm" c="#64748b" data-id="element-136">
            Track the building progress in real-time
          </Text>
        </Box>

        {/* Toggle Switch */}
        <SegmentedControl value={view} onChange={value => setView(value as 'project' | 'unit')} data={[{
        label: 'Project Progress',
        value: 'project'
      }, {
        label: 'My Unit Progress',
        value: 'unit'
      }]} fullWidth radius="md" size="md" styles={{
        root: {
          backgroundColor: '#f1f5f9'
        },
        indicator: {
          backgroundColor: '#1a2332'
        },
        label: {
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(26, 35, 50, 0.05)'
          },
          '&[data-active]': {
            color: 'white',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }
        }
      }} data-id="element-137" />

        {/* Overall Progress Card */}
        <Card shadow="sm" p="lg" radius="md" style={{
        background: 'linear-gradient(135deg, #1a2332 0%, #2d3a4f 100%)'
      }} data-id="element-138">
          <Group justify="space-between" align="center" mb="md" data-id="element-139">
            <Stack gap={2} data-id="element-140">
              <Text size="sm" c="rgba(255,255,255,0.7)" data-id="element-141">
                {view === 'project' ? 'Overall Project' : 'Your Unit'}{' '}
                Completion
              </Text>
              <Text fw={700} size="xl" c="white" data-id="element-142">
                {overallProgress}% Complete
              </Text>
            </Stack>
            <Box p={12} style={{
            backgroundColor: 'rgba(16, 185, 129, 0.2)',
            borderRadius: 12
          }} data-id="element-143">
              <Text fw={700} size="xl" c="#10b981" data-id="element-144">
                {overallProgress}%
              </Text>
            </Box>
          </Group>
          <Progress value={overallProgress} size="lg" radius="xl" color="#10b981" styles={{
          root: {
            backgroundColor: 'rgba(255,255,255,0.2)'
          }
        }} data-id="element-145" />
        </Card>

        {/* Timeline */}
        <Stack gap="sm" data-id="element-146">
          <Text fw={700} size="lg" c="#1a2332" data-id="element-147">
            Milestones
          </Text>
          <Stack gap={0} data-id="element-148">
            {milestones.map((milestone, index) => {
            const isComplete = milestone.progress === 100;
            const isInProgress = milestone.progress > 0 && milestone.progress < 100;
            const isLast = index === milestones.length - 1;
            return <Box key={milestone.id} style={{
              position: 'relative'
            }} data-id="element-149">
                  {/* Timeline Line */}
                  {!isLast && <Box style={{
                position: 'absolute',
                left: 15,
                top: 32,
                bottom: 0,
                width: 2,
                backgroundColor: isComplete ? '#10b981' : '#e2e8f0'
              }} data-id="element-150" />}

                  <Group gap="md" align="flex-start" pb="lg" data-id="element-151">
                    {/* Timeline Dot */}
                    <Box style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: isComplete ? '#10b981' : isInProgress ? '#fef3c7' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isInProgress ? '2px solid #f59e0b' : 'none',
                  flexShrink: 0
                }} data-id="element-152">
                      {isComplete ? <CheckCircle2 size={18} color="white" data-id="element-153" /> : <Circle size={12} color={isInProgress ? '#f59e0b' : '#94a3b8'} fill={isInProgress ? '#f59e0b' : 'none'} data-id="element-154" />}
                    </Box>

                    {/* Milestone Content */}
                    <Card p="md" radius="md" withBorder style={{
                  flex: 1,
                  borderColor: isInProgress ? '#fde68a' : '#e2e8f0'
                }} data-id="element-155">
                      <Stack gap="sm" data-id="element-156">
                        <Group justify="space-between" align="flex-start" data-id="element-157">
                          <Stack gap={2} data-id="element-158">
                            <Text fw={600} size="sm" c="#1a2332" data-id="element-159">
                              {milestone.title}
                            </Text>
                            <Text size="xs" c="#64748b" data-id="element-160">
                              {milestone.description}
                            </Text>
                          </Stack>
                          <Text fw={600} size="sm" c={isComplete ? '#10b981' : isInProgress ? '#f59e0b' : '#64748b'} data-id="element-161">
                            {milestone.progress}%
                          </Text>
                        </Group>

                        <Progress value={milestone.progress} size="sm" radius="xl" color={isComplete ? '#10b981' : '#f59e0b'} styles={{
                      root: {
                        backgroundColor: '#e2e8f0'
                      }
                    }} data-id="element-162" />

                        <Text size="xs" c="#94a3b8" data-id="element-163">
                          {milestone.completedDate ? `Completed: ${formatDate(milestone.completedDate)}` : `Expected: ${formatDate(milestone.expectedDate)}`}
                        </Text>

                        {/* Milestone Photos */}
                        {milestone.photos && milestone.photos.length > 0 && <Box data-id="element-164">
                            <Group gap={4} mb={6} data-id="element-165">
                              <Camera size={14} color="#64748b" data-id="element-166" />
                              <Text size="xs" c="#64748b" fw={500} data-id="element-167">
                                {milestone.photos.length}{' '}
                                {milestone.photos.length === 1 ? 'Photo' : 'Photos'}
                              </Text>
                            </Group>
                            <ScrollArea data-id="element-168">
                              <Group gap="xs" wrap="nowrap" data-id="element-169">
                                {milestone.photos.map((photo, photoIndex) => <Box key={photoIndex} style={{
                            borderRadius: 8,
                            overflow: 'hidden',
                            width: milestone.photos!.length === 1 ? '100%' : 120,
                            height: 80,
                            flexShrink: 0
                          }} data-id="element-170">
                                    <Image src={photo} alt={`${milestone.title} photo ${photoIndex + 1}`} h={80} w={milestone.photos!.length === 1 ? '100%' : 120} fit="cover" data-id="element-171" />
                                  </Box>)}
                              </Group>
                            </ScrollArea>
                          </Box>}
                      </Stack>
                    </Card>
                  </Group>
                </Box>;
          })}
          </Stack>
        </Stack>

        {/* Photo Gallery */}
        <Stack gap="sm" data-id="element-172">
          <Group justify="space-between" align="center" data-id="element-173">
            <Group gap="sm" data-id="element-174">
              <Camera size={18} color="#1a2332" data-id="element-175" />
              <Text fw={700} size="lg" c="#1a2332" data-id="element-176">
                All Site Photos
              </Text>
            </Group>
            <Text size="sm" c="#10b981" fw={500} style={{
            cursor: 'pointer'
          }} data-id="element-177">
              View All
            </Text>
          </Group>
          <SimpleGrid cols={2} spacing="sm" data-id="element-178">
            {galleryPhotos.map((photo, index) => <Box key={index} style={{
            borderRadius: 12,
            overflow: 'hidden',
            height: 120
          }} data-id="element-179">
                <Image src={photo} alt={`Construction photo ${index + 1}`} h={120} fit="cover" data-id="element-180" />
              </Box>)}
          </SimpleGrid>
        </Stack>
      </Stack>
    </ScrollArea>;
}